import type { ComponentPropsWithoutRef } from 'react';

import { cn } from '@/shared/utils/cn';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface SkeletonProps extends ComponentPropsWithoutRef<'div'> {
  /**
   * Forma do skeleton.
   * - `rect` — retangular com borda arredondada (padrão)
   * - `circle` — completamente circular (para avatares e ícones)
   */
  readonly shape?: 'rect' | 'circle';
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Atom Skeleton — placeholder animado para estados de loading.
 *
 * Segue o padrão visual de pulse (fade in/out) usando tokens de cor do
 * design system. Personalize dimensões via `className`.
 *
 * @example
 * ```tsx
 * // Linha de texto
 * <Skeleton className="h-mini w-3/4" />
 *
 * // Avatar circular
 * <Skeleton shape="circle" className="size-md" />
 *
 * // Bloco de card
 * <Skeleton className="h-xl w-full" />
 * ```
 */
export function Skeleton({ className, shape = 'rect', ...props }: SkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Carregando..."
      className={cn(
        'animate-pulse bg-ctx-layout-border',
        shape === 'circle' ? 'rounded-circular' : 'rounded-sm',
        className,
      )}
      {...props}
    />
  );
}
